import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Search, Package, Upload, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import { formatPrice } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyVariant = () => ({ id: undefined, size: '', design: '', price: '', sale_price: '', stock: '0', sku: '' });

const uniq = (arr) => [...new Set(arr)];

export default function AdminProducts() {
  const { getAuthHeaders } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    sku: '',
    images: '',
    is_featured: false,
    is_active: true
  });
  // Variants: explicit rows { id, size, design, price, sale_price, stock, sku }
  const [variants, setVariants] = useState([emptyVariant()]);
  // Per-option-value images: { Size: { value: url }, Design: { value: url } }
  const [optionValueImages, setOptionValueImages] = useState({});

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const response = await axios.get(`${API_URL}/admin/products?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/admin/products`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/categories`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchProducts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const imageList = formData.images ? formData.images.split('\n').filter((i) => i.trim()) : [];

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/admin/upload`, fd, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(res.data.url);
      }
      setFormData((prev) => {
        const existing = prev.images ? prev.images.split('\n').filter((i) => i.trim()) : [];
        return { ...prev, images: [...existing, ...uploaded].join('\n') };
      });
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (url) => {
    setFormData((prev) => {
      const existing = prev.images ? prev.images.split('\n').filter((i) => i.trim()) : [];
      return { ...prev, images: existing.filter((i) => i !== url).join('\n') };
    });
  };

  // ---- Variant helpers ----
  const addVariant = () => setVariants((prev) => [...prev, emptyVariant()]);
  const removeVariant = (idx) =>
    setVariants((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const updateVariant = (idx, field, value) =>
    setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, [field]: value } : v)));

  // Distinct non-empty Size / Design values across variant rows
  const sizeValues = uniq(variants.map((v) => (v.size || '').trim()).filter(Boolean));
  const designValues = uniq(variants.map((v) => (v.design || '').trim()).filter(Boolean));

  // ---- Option value images ----
  const uploadOptionValueImage = async (typeName, value, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/admin/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setOptionValueImages((prev) => ({
        ...prev,
        [typeName]: { ...(prev[typeName] || {}), [value]: res.data.url }
      }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeOptionValueImage = (typeName, value) => {
    setOptionValueImages((prev) => {
      const forType = { ...(prev[typeName] || {}) };
      delete forType[value];
      return { ...prev, [typeName]: forType };
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      sku: '',
      images: '',
      is_featured: false,
      is_active: true
    });
    setVariants([emptyVariant()]);
    setOptionValueImages({});
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      sku: product.sku || '',
      images: product.images?.join('\n') || '',
      is_featured: product.is_featured,
      is_active: product.is_active
    });
    setVariants(
      (product.variants || []).length > 0
        ? product.variants.map((v) => ({
            id: v.id,
            size: v.options?.Size || '',
            design: v.options?.Design || '',
            price: v.price != null ? v.price.toString() : '',
            sale_price: v.sale_price != null ? v.sale_price.toString() : '',
            stock: v.stock != null ? v.stock.toString() : '0',
            sku: v.sku || ''
          }))
        : [emptyVariant()]
    );
    setOptionValueImages(product.option_value_images || {});
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (variants.length === 0) {
      toast.error('Please add at least one variant');
      return;
    }

    const usedSize = sizeValues.length > 0;
    const usedDesign = designValues.length > 0;

    // Validation: price required, consistent dimensions, no duplicate combos
    const combos = new Set();
    for (const v of variants) {
      const size = (v.size || '').trim();
      const design = (v.design || '').trim();
      const label = [size && `Size ${size}`, design && `Design ${design}`].filter(Boolean).join(' / ') || 'this variant';

      if (v.price === '' || isNaN(parseFloat(v.price))) {
        toast.error(`Enter a valid price for ${label}`);
        return;
      }
      if (usedSize && !size) {
        toast.error('Every variant must have a Size (or clear Size on all variants)');
        return;
      }
      if (usedDesign && !design) {
        toast.error('Every variant must have a Design (or clear Design on all variants)');
        return;
      }
      const key = `${size}||${design}`;
      if (combos.has(key)) {
        toast.error(`Duplicate combination: ${label}`);
        return;
      }
      combos.add(key);
    }

    const optionTypes = [];
    if (usedSize) optionTypes.push({ name: 'Size', values: sizeValues });
    if (usedDesign) optionTypes.push({ name: 'Design', values: designValues });

    // Prune option value images to only used values
    const cleanedImages = {};
    if (usedSize) {
      const map = optionValueImages.Size || {};
      const vm = {};
      sizeValues.forEach((v) => { if (map[v]) vm[v] = map[v]; });
      if (Object.keys(vm).length) cleanedImages.Size = vm;
    }
    if (usedDesign) {
      const map = optionValueImages.Design || {};
      const vm = {};
      designValues.forEach((v) => { if (map[v]) vm[v] = map[v]; });
      if (Object.keys(vm).length) cleanedImages.Design = vm;
    }

    const productData = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      sku: formData.sku || null,
      images: formData.images ? formData.images.split('\n').filter((i) => i.trim()) : [],
      option_types: optionTypes,
      option_value_images: cleanedImages,
      variants: variants.map((v) => {
        const options = {};
        if (usedSize) options.Size = (v.size || '').trim();
        if (usedDesign) options.Design = (v.design || '').trim();
        return {
          id: v.id || undefined,
          options,
          price: parseFloat(v.price),
          sale_price: v.sale_price !== '' ? parseFloat(v.sale_price) : null,
          stock: parseInt(v.stock || '0', 10),
          sku: v.sku || null
        };
      }),
      is_featured: formData.is_featured,
      is_active: formData.is_active
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/admin/products/${editingProduct.id}`, productData, {
          headers: getAuthHeaders()
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/products`, productData, {
          headers: getAuthHeaders()
        });
        toast.success('Product created successfully');
      }
      fetchProducts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/admin/products/${productId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const renderOptionImages = (typeName, values) => (
    <div data-testid={`option-images-${typeName}`}>
      <Label className="text-sm">{typeName}</Label>
      <div className="flex flex-wrap gap-4 mt-2">
        {values.map((value) => {
          const img = optionValueImages[typeName]?.[value];
          return (
            <div key={value} className="text-center">
              {img ? (
                <div className="relative group">
                  <img src={img} alt={value} className="w-20 h-20 rounded-lg object-cover border" />
                  <button
                    type="button"
                    onClick={() => removeOptionValueImage(typeName, value)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-option-image-${typeName}-${value}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label
                  className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex items-center justify-center cursor-pointer hover:border-[#EA580C] hover:bg-muted/30 transition-colors"
                  data-testid={`option-image-upload-${typeName}-${value}`}
                >
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadOptionValueImage(typeName, value, f);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              <span className="text-xs text-muted-foreground mt-1 block">{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="p-6" data-testid="admin-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Products
          </h1>
          <p className="text-muted-foreground mt-1">{products.length} products</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full" data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Base product details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="sku">Base SKU</Label>
                  <Input
                    id="sku"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder="Optional product code"
                    data-testid="product-sku-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="mt-1" data-testid="product-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="mt-1"
                    data-testid="product-description-input"
                  />
                </div>
              </div>

              {/* Variants */}
              <div className="border border-border rounded-xl p-4 space-y-3" data-testid="variants-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Variants</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add a row per Size / Design combination. Leave Size or Design blank if a product doesn't use it.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addVariant} data-testid="add-variant-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Variant
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-2 pr-3">Size</th>
                        <th className="py-2 pr-3">Design</th>
                        <th className="py-2 pr-3">Price *</th>
                        <th className="py-2 pr-3">Sale Price</th>
                        <th className="py-2 pr-3">Stock</th>
                        <th className="py-2 pr-3">SKU</th>
                        <th className="py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((v, idx) => (
                        <tr key={idx} data-testid={`variant-row-${idx}`}>
                          <td className="py-1 pr-3">
                            <Input
                              value={v.size}
                              onChange={(e) => updateVariant(idx, 'size', e.target.value)}
                              className="w-24"
                              placeholder="M"
                              data-testid={`variant-size-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-3">
                            <Input
                              value={v.design}
                              onChange={(e) => updateVariant(idx, 'design', e.target.value)}
                              className="w-32"
                              placeholder="Floral"
                              data-testid={`variant-design-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={v.price}
                              onChange={(e) => updateVariant(idx, 'price', e.target.value)}
                              className="w-24"
                              required
                              data-testid={`variant-price-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={v.sale_price}
                              onChange={(e) => updateVariant(idx, 'sale_price', e.target.value)}
                              className="w-24"
                              data-testid={`variant-sale-price-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-3">
                            <Input
                              type="number"
                              value={v.stock}
                              onChange={(e) => updateVariant(idx, 'stock', e.target.value)}
                              className="w-20"
                              data-testid={`variant-stock-${idx}`}
                            />
                          </td>
                          <td className="py-1 pr-3">
                            <Input
                              value={v.sku}
                              onChange={(e) => updateVariant(idx, 'sku', e.target.value)}
                              className="w-32"
                              placeholder="Optional"
                              data-testid={`variant-sku-${idx}`}
                            />
                          </td>
                          <td className="py-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeVariant(idx)}
                              disabled={variants.length <= 1}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`remove-variant-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Option Images (per value) */}
              {(sizeValues.length > 0 || designValues.length > 0) && (
                <div className="border border-border rounded-xl p-4 space-y-4" data-testid="option-images-section">
                  <div>
                    <Label className="text-base font-semibold">
                      Option Images <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Assign a photo to a Design or Size value. It's shown to shoppers when they select it.
                    </p>
                  </div>
                  {designValues.length > 0 && renderOptionImages('Design', designValues)}
                  {sizeValues.length > 0 && renderOptionImages('Size', sizeValues)}
                </div>
              )}

              {/* Images */}
              <div>
                <Label>Product Images</Label>
                <div className="mt-2 flex flex-wrap gap-3">
                  {imageList.map((url) => (
                    <div key={url} className="relative group">
                      <img src={url} alt="product" className="w-20 h-20 rounded-lg object-cover border" />
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="remove-image-btn"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA580C] hover:bg-muted/30 transition-colors" data-testid="product-image-upload">
                    {uploading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground mt-1">Upload</span>
                      </>
                    )}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
                <Textarea
                  id="images"
                  name="images"
                  value={formData.images}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="...or paste image URLs (one per line)"
                  className="mt-3 text-xs"
                />
              </div>

              {/* Flags */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                  />
                  <Label htmlFor="is_featured">Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white" data-testid="save-product-btn">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="product-search-input"
          />
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Product</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">SKU</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Category</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Variants</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Price</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Stock</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p>No products found</p>
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const hasRange = product.price_to > product.price_from;
                  return (
                    <tr key={product.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`product-row-${product.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={product.images?.[0] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=80'}
                            alt={product.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.is_featured && (
                              <span className="text-xs text-[#EA580C]">Featured</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 mono text-sm">{product.sku || '—'}</td>
                      <td className="p-4 text-sm">{product.category}</td>
                      <td className="p-4 text-sm">{product.variants?.length || 0}</td>
                      <td className="p-4">
                        <p className="font-medium">
                          {hasRange && <span className="text-xs text-muted-foreground mr-1">from</span>}
                          {formatPrice(product.price_from)}
                        </p>
                      </td>
                      <td className="p-4">
                        <span className={`${product.total_stock < 10 ? 'text-amber-600 font-medium' : ''}`}>
                          {product.total_stock}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`status-badge ${product.is_active ? 'delivered' : 'cancelled'}`}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(product.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
